/**
 * La ayuda: el manual de la aplicacion, dentro de la aplicacion.
 *
 * ===========================================================================
 * ESTA ORGANIZADA POR TAREAS, NO POR PANTALLAS
 *
 * La version anterior era un indice de zonas --"esto es Cotizaciones, esto es
 * RFQ"-- y contestaba una sola pregunta: donde estoy. Las que llegan de verdad
 * son otras, y ninguna se contesta nombrando una zona:
 *
 *   por que esta tarifa no me sale
 *   que quiere decir este aviso ambar
 *   por que le cobran 500 kilos si pesa 120
 *   por que el total de esta no se puede comparar con el de aquella
 *   ya la cotice y el precio cambio, ¿que pasa con el documento?
 *
 * Asi que las secciones son las tareas --buscar, leer, cotizar-- y el indice de
 * zonas se queda como una de ellas, la de "donde estoy".
 *
 * ===========================================================================
 * NO REDACTA NADA QUE LA PANTALLA YA DIGA
 *
 * Es la decision de diseño de este archivo y la razon de que sea corto.
 *
 * Las banderas, las causas de una busqueda vacia y los motivos por los que un
 * cargo no se valora se enumeran LEYENDO LAS MISMAS CLAVES DE i18n que usa la
 * pantalla --`bandera.EMBARGO`, `vacio.causa.VENCIDA.hacer`, `motivoFuera.*`--
 * y no una redaccion propia del manual.
 *
 * Un manual que reescribe con sus palabras lo que la pantalla dice con las
 * suyas empieza igual y se separa: se corrige un aviso, nadie se acuerda de la
 * ayuda, y a partir de ahi el manual explica algo que la aplicacion ya no dice.
 * Eso es peor que no tener manual, porque se lee con confianza.
 *
 * Lo que SI se redacta aqui es lo que no cabe en una pantalla: el porque. "Una
 * vigencia ausente cuenta como vencida" es una regla del dominio que ningun
 * aviso puede explicar entera mientras el agente esta comparando precios.
 *
 * ===========================================================================
 * LO QUE NO ABRE LA CUENTA DE QUIEN MIRA, NO SE EXPLICA
 *
 * Las secciones se filtran por CAPACIDAD, igual que el menu [zonas.ts]. Quien
 * no cotiza no lee tres pantallas sobre como se cotiza, y sobre todo no acaba
 * pidiendo por que no le sale un boton que su rol no tiene.
 *
 * Como el menu, esto NO es la cerradura: es no enseñar puertas que no abren.
 * ===========================================================================
 */

import { BANDERAS, CAUSAS_SIN_RESULTADOS, MOTIVOS_FUERA_DEL_TOTAL } from '../api';
import type { Capacidad } from '../api';
import { el } from '../dom';
import type { Traductor } from '../i18n';
import { rutaDeZona, zonasVisibles, type Zona } from '../zonas';

export interface OpcionesAyuda {
  readonly t: Traductor;
  readonly capacidades: readonly Capacidad[];
  /** Ir a una zona desde un enlace del manual. */
  readonly onIrA: (zona: Zona) => void;
}

/**
 * Una seccion del manual.
 *
 * `capacidad` nula significa que la lee todo el mundo. `cuerpo` devuelve los
 * bloques de dentro; el titulo y el enganche del indice los pone `pintarAyuda`,
 * para que ninguna seccion pueda quedarse sin ancla.
 */
interface Seccion {
  readonly id: string;
  readonly capacidad: Capacidad | null;
  cuerpo(o: OpcionesAyuda): (HTMLElement | null)[];
}

const SECCIONES: readonly Seccion[] = [
  { id: 'buscar', capacidad: 'catalogo.consultar', cuerpo: seccionBuscar },
  { id: 'resultados', capacidad: 'catalogo.consultar', cuerpo: seccionResultados },
  { id: 'peso', capacidad: 'catalogo.consultar', cuerpo: seccionPeso },
  { id: 'cotizar', capacidad: 'cotizar', cuerpo: seccionCotizar },
  { id: 'vacio', capacidad: 'catalogo.consultar', cuerpo: seccionVacio },
  // El indice de zonas va al final y lo lee todo el mundo: es la unica que no
  // enseña a hacer nada, y quien entra por primera vez llega a ella igual por
  // el indice de arriba.
  { id: 'zonas', capacidad: null, cuerpo: seccionZonas },
];

export function pintarAyuda(o: OpcionesAyuda): HTMLElement {
  const { t } = o;
  const mias = SECCIONES.filter(
    (s) => s.capacidad === null || o.capacidades.includes(s.capacidad),
  );

  return el('section', { clase: 'panel panel-ayuda' }, [
    el('h2', { texto: t('zona.ayuda.nombre') }),
    el('p', { clase: 'ayuda-intro', texto: t('ayuda.intro') }),

    // El indice. Con seis secciones largas, sin el hay que recorrer la pagina
    // entera para saber si lo que se busca esta.
    el('nav', { clase: 'ayuda-indice', attrs: { 'aria-label': t('ayuda.indice') } }, [
      el(
        'ul',
        {},
        mias.map((s) =>
          el('li', {}, [
            el('a', {
              texto: t(`ayuda.${s.id}.titulo`),
              attrs: { href: `#ayuda-${s.id}` },
            }),
          ]),
        ),
      ),
    ]),

    ...mias.map((s) =>
      el('article', { clase: 'ayuda-seccion', attrs: { id: `ayuda-${s.id}` } }, [
        el('h3', { texto: t(`ayuda.${s.id}.titulo`) }),
        ...s.cuerpo(o),
      ]),
    ),
  ]);
}

// ---------------------------------------------------------------------------
// Piezas
// ---------------------------------------------------------------------------

/** Un parrafo del manual. */
function p(t: Traductor, clave: string): HTMLElement {
  return el('p', { texto: t(clave) });
}

/**
 * Una lista de terminos y su explicacion, con los textos SACADOS DE LA PANTALLA.
 *
 * `clave` construye la clave de i18n de cada codigo. Ver la cabecera: el manual
 * no reescribe estos textos, los lee de donde ya estan.
 */
function glosario(
  t: Traductor,
  codigos: readonly string[],
  clave: (codigo: string) => string,
  extra?: (codigo: string) => string | null,
): HTMLElement {
  return el(
    'dl',
    { clase: 'ayuda-glosario' },
    codigos.flatMap((c) => [
      el('dt', {}, [el('code', { texto: c })]),
      el('dd', {}, [
        el('span', { texto: t(clave(c)) }),
        (() => {
          const mas = extra?.(c) ?? null;
          return mas === null ? null : el('p', { clase: 'tenue', texto: mas });
        })(),
      ]),
    ]),
  );
}

// ---------------------------------------------------------------------------
// Secciones
// ---------------------------------------------------------------------------

function seccionBuscar(o: OpcionesAyuda): (HTMLElement | null)[] {
  const { t } = o;
  return [
    p(t, 'ayuda.buscar.intro'),

    el('h4', { texto: t('ayuda.buscar.modos') }),
    p(t, 'ayuda.buscar.maritimo'),
    p(t, 'ayuda.buscar.aereo'),

    el('h4', { texto: t('ayuda.buscar.fecha') }),
    p(t, 'ayuda.buscar.fechaPorQue'),

    el('h4', { texto: t('ayuda.buscar.pais') }),
    // [Invariante 6][H51] Es la regla que evita mandar la carga al continente
    // equivocado, y la que mas desconcierta la primera vez que salta.
    p(t, 'ayuda.buscar.paisPorQue'),

    el('h4', { texto: t('ayuda.buscar.carga') }),
    p(t, 'ayuda.buscar.cargaPorQue'),
    // La ausencia de filtro es deliberada y hay que decirla: quien declara
    // PELIGROSA y ve las mismas tarifas de antes puede pensar que no se guardo.
    el('div', { clase: 'aviso aviso-neutro' }, [
      el('p', { texto: t('ayuda.buscar.noFiltra') }),
    ]),
  ];
}

function seccionResultados(o: OpcionesAyuda): (HTMLElement | null)[] {
  const { t } = o;
  return [
    p(t, 'ayuda.resultados.intro'),

    el('h4', { texto: t('ayuda.resultados.grupos') }),
    p(t, 'ayuda.resultados.gruposPorQue'),

    el('h4', { texto: t('ayuda.resultados.avisos') }),
    p(t, 'ayuda.resultados.avisosPorQue'),
    // Los textos son los MISMOS que salen en la fila. Ver la cabecera.
    glosario(t, BANDERAS, (b) => `bandera.${b}`),

    el('h4', { texto: t('ayuda.resultados.costos') }),
    p(t, 'ayuda.resultados.costosPorQue'),
  ];
}

function seccionPeso(o: OpcionesAyuda): (HTMLElement | null)[] {
  const { t } = o;
  return [
    p(t, 'ayuda.peso.intro'),

    el('h4', { texto: t('ayuda.peso.cual') }),
    p(t, 'ayuda.peso.cualPorQue'),
    // Un ejemplo con numeros. Es la seccion donde mas ayuda: la regla en
    // abstracto se entiende y no se recuerda; "120 kg pagan 500" se recuerda.
    el('div', { clase: 'aviso aviso-neutro ayuda-ejemplo' }, [
      el('p', { texto: t('ayuda.peso.ejemplo') }),
    ]),

    el('h4', { texto: t('ayuda.peso.escalon') }),
    p(t, 'ayuda.peso.escalonPorQue'),

    el('h4', { texto: t('ayuda.peso.bultos') }),
    p(t, 'ayuda.peso.bultosPorQue'),
  ];
}

function seccionCotizar(o: OpcionesAyuda): (HTMLElement | null)[] {
  const { t } = o;
  return [
    p(t, 'ayuda.cotizar.intro'),

    el('h4', { texto: t('ayuda.cotizar.congela') }),
    // [Invariante 5] Es lo que hace que releer una cotizacion no la cambie, y
    // lo que sorprende cuando el catalogo ya dice otro precio.
    p(t, 'ayuda.cotizar.congelaPorQue'),

    el('h4', { texto: t('ayuda.cotizar.noValorados') }),
    p(t, 'ayuda.cotizar.noValoradosPorQue'),
    glosario(t, MOTIVOS_FUERA_DEL_TOTAL, (m) => `motivoFuera.${m}`),

    el('h4', { texto: t('ayuda.cotizar.aprobacion') }),
    p(t, 'ayuda.cotizar.aprobacionPorQue'),
  ];
}

function seccionVacio(o: OpcionesAyuda): (HTMLElement | null)[] {
  const { t } = o;
  return [
    p(t, 'ayuda.vacio.intro'),
    // Titulo y QUE HACER de cada causa, tal como los pinta la pantalla vacia.
    // El "que hacer" es lo util: la causa se lee alli y aqui se consulta antes.
    glosario(
      t,
      CAUSAS_SIN_RESULTADOS,
      (c) => `vacio.causa.${c}.titulo`,
      (c) => t(`vacio.causa.${c}.hacer`),
    ),
  ];
}

function seccionZonas(o: OpcionesAyuda): (HTMLElement | null)[] {
  const { t } = o;
  return [
    p(t, 'ayuda.zonas.intro'),
    el(
      'dl',
      { clase: 'ayuda-zonas' },
      zonasVisibles(o.capacidades).flatMap((z) => [
        el('dt', {}, [
          el('a', {
            texto: t(`zona.${z.id}.nombre`),
            attrs: { href: rutaDeZona(z.id) },
            onClick: (ev) => {
              ev.preventDefault();
              o.onIrA(z.id);
            },
          }),
        ]),
        el('dd', { texto: t(`zona.${z.id}.descripcion`) }),
      ]),
    ),
  ];
}
